/**
 * In-app confirmation modal. Replaces `window.confirm` / `window.prompt`
 * for destructive actions — those native dialogs are unreliable in
 * installed iOS PWAs (standalone WebKit makes `window.prompt` a no-op
 * that returns null without ever showing UI, which silently swallowed
 * the "type the name to confirm" member-removal gate).
 *
 * When `confirmPhrase` is set, the action stays disabled until the user
 * types it exactly — the same typed-confirmation guard, but with a real
 * input that works on every surface.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { TextField } from './TextField';

interface Props {
  title: string;
  /** Body copy. String or any node (e.g. a styled warning block). */
  message: React.ReactNode;
  /** If set, the confirm button stays disabled until this is typed exactly. */
  confirmPhrase?: string;
  confirmLabel?: string;
  /** Retained for call-site compatibility. The confirm button now always
   *  uses the oxblood accent (the app's single destructive/primary color),
   *  so this no longer changes styling. */
  destructive?: boolean;
  busy?: boolean;
  /** Surfaced in red inside the dialog; the dialog stays open so the
   *  user can retry without losing the confirmation they typed. */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmPhrase,
  confirmLabel = 'Confirm',
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}: Props) {
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Focus the phrase input (or the dialog) on open, and let Escape cancel.
  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const phraseOk = !confirmPhrase || typed.trim() === confirmPhrase;
  const canConfirm = phraseOk && !busy;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/40 p-4"
      onClick={() => !busy && onCancel()}
    >
      <div
        className="card w-full max-w-sm p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-xl text-ink">{title}</h2>
        <div className="text-soft text-sm leading-relaxed">{message}</div>

        {confirmPhrase && (
          <TextField
            ref={inputRef}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmPhrase}
            autoCapitalize="off"
            autoCorrect="off"
          />
        )}

        {error && <p className="text-oxblood text-sm">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onConfirm}
            disabled={!canConfirm}
            busy={busy}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
